import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  getCatalogueBooks,
  getUserBooks,
  getRecomendedBooks,
  postCreateUserBook,
  postAddBook,
  getValidateLicense,
  getBook,
  getBookResources,
  sendRequestResource,
  getOldUserBooks,
  patchArchiveBook,
  patchUnarchiveBook,
  getArchivedBooks,
  orderBooks,
} from 'api/book'
import { hideLoader } from 'store/actions/drawer.actions'
import { TYPES_ALERTS } from 'utils/constants'
import { useSnackBarFeedback } from 'context/snackbarFeedback'
import { USER_KEYS } from 'hooks/user'
import { CLASSROOM_KEYS } from 'hooks/classroom'
import { KEY_HOME } from 'hooks/home'

export const KEY_GET_USERBOOKS = 'USERBOOKS'
export const KEY_GET_OLD_USERBOOKS = 'OLD-USERBOOKS'
export const KEY_GET_BOOK_UNITS = 'BOOK-UNITS'
export const KEY_GET_CATALOGUE_BOOKS = 'CATALOGUE_BOOKS'
export const KEY_GET_RECOMENDED_BOOKS = 'RECOMENDED_BOOKS'
export const KEY_GET_VALIDATE_LICENSE = 'VALIDATE_LICENSE'
export const KEY_BOOK = 'BOOK'
export const KEY_BOOK_RESOURCES = 'BOOK_RESOURCES'
export const KEY_REQUEST_RESOURCE = 'REQUEST_RESOURCE'
export const KEY_GET_ARCHIVED_BOOKS = 'ARCHIVED_BOOKS'

export const useGetUserBooks = (isNewEndpoint = false, enabled = true) => {
  const dispatch = useDispatch()
  const getUserBooksLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getUserBooks(isNewEndpoint)
  }
  return useQuery([KEY_GET_USERBOOKS], getUserBooksLoaderHidden, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}

export const useGetCatalogueBooks = () => {
  const dispatch = useDispatch()
  const getCatalogueBookLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getCatalogueBooks()
  }
  return useQuery([KEY_GET_CATALOGUE_BOOKS], getCatalogueBookLoaderHidden, {
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}

export const useGetRecomendedBooks = (enabled) => {
  const dispatch = useDispatch()
  const getRecomendedBooksLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getRecomendedBooks()
  }
  return useQuery([KEY_GET_RECOMENDED_BOOKS], getRecomendedBooksLoaderHidden, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}

export const usePostCreateUserBook = () => {
  return useMutation(({ bookId, teacherId }) =>
    postCreateUserBook({ bookId, teacherId })
  )
}

export const usePostAddBook = () => {
  const { t } = useTranslation()
  const addFeedback = useSnackBarFeedback()
  const queryClient = useQueryClient()

  return useMutation(
    ({
      childBookId,
      isoCountry,
      licenseCode,
      schoolYearId,
      targetId,
      userRole,
    }) =>
      postAddBook({
        childBookId,
        isoCountry,
        licenseCode,
        schoolYearId,
        targetId,
        userRole,
      }),
    {
      onError: () => {
        const id = 'error-feedback-add-book'
        addFeedback({
          dataTestId: id,
          id,
          text: t('Lo sentimos, ha habido un error al añadir el libro'),
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSuccess: () => {
        const id = 'success-feedback-add-book'
        addFeedback({
          dataTestId: id,
          id,
          text: t('Libro añadido correctamente'),
          variant: TYPES_ALERTS.SUCCESS,
        })
        queryClient.invalidateQueries({
          queryKey: [KEY_GET_USERBOOKS],
        })
        queryClient.invalidateQueries({
          queryKey: [KEY_GET_CATALOGUE_BOOKS],
        })
        queryClient.invalidateQueries({
          queryKey: [USER_KEYS.USER_CONFIGURATIONS],
        })
        queryClient.invalidateQueries({
          queryKey: [CLASSROOM_KEYS.AVAILABLE_CLASSES],
        })
        queryClient.invalidateQueries({
          queryKey: [KEY_HOME],
        })
        queryClient.invalidateQueries({
          queryKey: [KEY_GET_RECOMENDED_BOOKS],
        })
      },
    }
  )
}

export const useGetValidateLicense = ({ enabled, licenseCode, userId }) => {
  const dispatch = useDispatch()
  const getValidateLicenseLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getValidateLicense({ licenseCode, userId })
  }
  return useQuery({
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    queryFn: getValidateLicenseLoaderHidden,
    queryKey: [KEY_GET_VALIDATE_LICENSE],
  })
}

export const useGetBook = ({ bookId }) => {
  const dispatch = useDispatch()
  const LoaderHidden = () => {
    dispatch(hideLoader(true))
    return getBook({ bookId })
  }
  return useQuery([KEY_BOOK, bookId], LoaderHidden, {
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}

export const useGetBookResources = ({ bookId, email, enabled }) => {
  const dispatch = useDispatch()
  const LoaderHidden = () => {
    dispatch(hideLoader(true))
    return getBookResources({ bookId, email })
  }
  return useQuery([KEY_BOOK_RESOURCES, bookId], LoaderHidden, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}

export const useSendRequestResources = ({ enabled, resourceId, url }) => {
  const dispatch = useDispatch()
  const LoaderHidden = () => {
    dispatch(hideLoader(true))
    return sendRequestResource({ url })
  }
  return useQuery([KEY_REQUEST_RESOURCE, resourceId], LoaderHidden, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
  })
}

export const useGetOldUserBooks = (enabled) => {
  const dispatch = useDispatch()
  const getOldUserBooksLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getOldUserBooks()
  }
  return useQuery([KEY_GET_OLD_USERBOOKS], getOldUserBooksLoaderHidden, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}

export const useGetArchivedBooks = (enabled) => {
  const dispatch = useDispatch()
  const getArchivedBooksLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getArchivedBooks()
  }
  return useQuery([KEY_GET_ARCHIVED_BOOKS], getArchivedBooksLoaderHidden, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}
export const usePatchArchiveBook = () => {
  const dispatch = useDispatch()
  const patchArchiveBookLoaderHidden = (userbookId) => {
    dispatch(hideLoader(true))
    return patchArchiveBook(userbookId)
  }
  return useMutation((userbookId) => patchArchiveBookLoaderHidden(userbookId), {
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
  })
}

export const usePatchUnarchiveBook = () => {
  const dispatch = useDispatch()
  const patchUnarchiveBookLoaderHidden = (userbookId) => {
    dispatch(hideLoader(true))
    return patchUnarchiveBook(userbookId)
  }
  return useMutation(
    (userbookId) => patchUnarchiveBookLoaderHidden(userbookId),
    {
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
    }
  )
}

export const usePostOrderBooks = () => {
  return useMutation(({ userBooks, userId, userRole }) => {
    const orderBooksIDs = userBooks.map((book, index) => ({
      order: index,
      userBookId: book.id,
    }))
    return orderBooks({ userBooks: orderBooksIDs, userId, userRole })
  })
}
